/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { QuestionDto } from '../models/QuestionDto';
import type { StartStudentPaperRequest } from '../models/StartStudentPaperRequest';
import type { StudentAssessmentDetailsResponse } from '../models/StudentAssessmentDetailsResponse';
import type { StudentAssessmentResultResponse } from '../models/StudentAssessmentResultResponse';
import type { StudentPaperAnswersRequest } from '../models/StudentPaperAnswersRequest';
import type { StudentPaperDto } from '../models/StudentPaperDto';
import type { StudentQuestionStatusResponse } from '../models/StudentQuestionStatusResponse';
import type { StudentUpcomingAssessmentResponse } from '../models/StudentUpcomingAssessmentResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class StudentAssessmentRestControllerService {
  /**
   * getAssessmentDetails
   * @returns StudentAssessmentDetailsResponse OK
   * @throws ApiError
   */
  public static getAssessmentDetailsUsingGet({
    paperId,
  }: {
    /**
     * paper-id
     */
    paperId: string;
  }): CancelablePromise<StudentAssessmentDetailsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/cbt/student/assessments/papers/{paper-id}/details',
      path: {
        'paper-id': paperId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getMyResult
   * @returns StudentAssessmentResultResponse OK
   * @throws ApiError
   */
  public static getMyResultUsingGet({
    paperId,
    studentId,
  }: {
    /**
     * paper-id
     */
    paperId: string;
    /**
     * student-id
     */
    studentId: string;
  }): CancelablePromise<StudentAssessmentResultResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/cbt/student/assessments/papers/{paper-id}/my-result',
      path: {
        'paper-id': paperId,
      },
      query: {
        'student-id': studentId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getQuestionStatuses
   * @returns StudentQuestionStatusResponse OK
   * @throws ApiError
   */
  public static getQuestionStatusesUsingGet({
    paperId,
    studentId,
  }: {
    /**
     * paper-id
     */
    paperId: string;
    /**
     * student-id
     */
    studentId: string;
  }): CancelablePromise<Array<StudentQuestionStatusResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/cbt/student/assessments/papers/{paper-id}/questions/status',
      path: {
        'paper-id': paperId,
      },
      query: {
        'student-id': studentId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * setQuestionFlag
   * @returns any OK
   * @throws ApiError
   */
  public static setQuestionFlagUsingPut({
    flagged,
    paperId,
    questionId,
    studentId,
  }: {
    /**
     * flagged
     */
    flagged: boolean;
    /**
     * paper-id
     */
    paperId: string;
    /**
     * question-id
     */
    questionId: string;
    /**
     * student-id
     */
    studentId: string;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/cbt/student/assessments/papers/{paper-id}/questions/{question-id}/flag',
      path: {
        'paper-id': paperId,
        'question-id': questionId,
      },
      query: {
        flagged: flagged,
        'student-id': studentId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getQuestion
   * @returns QuestionDto OK
   * @throws ApiError
   */
  public static getQuestionUsingGet2({
    paperId,
    questionIndex,
  }: {
    /**
     * paper-id
     */
    paperId: string;
    /**
     * question-index
     */
    questionIndex: number;
  }): CancelablePromise<QuestionDto> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/cbt/student/assessments/papers/{paper-id}/questions/{question-index}',
      path: {
        'paper-id': paperId,
        'question-index': questionIndex,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * saveAnswers
   * @returns any OK
   * @throws ApiError
   */
  public static saveAnswersUsingPut({
    request,
  }: {
    /**
     * request
     */
    request: StudentPaperAnswersRequest;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/cbt/student/assessments/save-answers',
      body: request,
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * startAssessment
   * @returns StudentPaperDto OK
   * @returns any Created
   * @throws ApiError
   */
  public static startAssessmentUsingPost({
    request,
  }: {
    /**
     * request
     */
    request: StartStudentPaperRequest;
  }): CancelablePromise<StudentPaperDto | any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/cbt/student/assessments/start',
      body: request,
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * submitAssessment
   * @returns any OK
   * @throws ApiError
   */
  public static submitAssessmentUsingPost({
    request,
  }: {
    /**
     * request
     */
    request: StudentPaperAnswersRequest;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/cbt/student/assessments/submit',
      body: request,
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getUpcomingAssessments
   * @returns StudentUpcomingAssessmentResponse OK
   * @throws ApiError
   */
  public static getUpcomingAssessmentsUsingGet({
    studentId,
    type,
  }: {
    /**
     * student-id
     */
    studentId: string;
    /**
     * type
     */
    type?: 'ALL' | 'ASSIGNMENT' | 'CBT_EXAM' | 'QUIZ';
  }): CancelablePromise<StudentUpcomingAssessmentResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/cbt/student/assessments/upcoming',
      query: {
        'student-id': studentId,
        type: type,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }
}
